import { useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import {
  getAktivitaGaKpiCompare,
  getAktivitaGaTrend,
  getAktivitaGaPages,
  type AktivitaPeriod,
  type AktivitaCountry,
  type GaKpiCompare,
  type GaTrend,
  type GaPages,
  type GaPageItem,
} from '../../api/rondo';
import { useTabParam } from '../../shell/useTabParam';
import { ArcSelect, ArcTabs, ArcToolbar } from '../../components/arc/primitives/arc-primitives';
import { Card } from '../../components/arc/card/card';
import { Button } from '../../components/arc/button/button';
import { PageState } from '../../components/arc/page-state/page-state';
import { SortableDataTable, type DataColumn } from '../../components/arc/sortable-data-table/sortable-data-table';
import {
  DeltaPlain,
  TrendChart,
  fmtNum,
  fmtDuration,
  fmtPct,
  fmtValue,
  type KpiFmt,
} from './aktivita-shared';
import './aktivita-drill.css';

const PERIOD_LABEL: Record<AktivitaPeriod, string> = {
  day: 'den',
  week: 'týden',
  month: 'měsíc',
  year: 'rok',
};

interface DrillKpi {
  key: keyof GaKpiCompare['current'];
  label: string;
  fmt: KpiFmt;
  lowerIsBetter?: boolean;
}
interface DrillTrend {
  key: keyof Omit<GaTrend['points'][number], 'date'>;
  label: string;
  color: string;
  fmt?: 'pct';
}
interface DrillPagesCfg {
  sort: 'views' | 'time' | 'bounce';
  columns: ('views' | 'time' | 'bounce')[];
  limit: number;
}
export interface DrillConfig {
  title?: string;
  subtitle?: string;
  pageClass?: string;
  kpis: DrillKpi[];
  trend: DrillTrend[];
  trendTitle?: string;
  pages: DrillPagesCfg;
  tableTitle: string;
}

export function AktivitaDrillPage({ config }: { config: DrillConfig }) {
  const [period, setPeriod] = useState<AktivitaPeriod>('month');
  const [country, setCountry] = useState<AktivitaCountry>('all');

  const [kpi, setKpi] = useState<GaKpiCompare | null>(null);
  const [trend, setTrend] = useState<GaTrend | null>(null);
  const [pages, setPages] = useState<GaPages | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getAktivitaGaKpiCompare({ period, country }),
      getAktivitaGaTrend({ period, country }),
      getAktivitaGaPages({ period, country, sort: config.pages.sort, limit: config.pages.limit }),
    ])
      .then(([k, t, p]) => {
        if (cancelled) return;
        setKpi(k);
        setTrend(t);
        setPages(p);
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, country]);

  function reset() {
    setPeriod('month');
    setCountry('all');
  }

  const cols = config.pages.columns;
  type PageRow = GaPageItem & { rank: number; title: string } & Record<string, unknown>;
  const pageRows: PageRow[] = (pages?.items ?? []).map((item, index) => ({ ...item, rank: index + 1, title: item.page_title || item.page_path }));
  const pageColumns: DataColumn<PageRow>[] = [
    { key: 'rank', label: '#', width: 65, numeric: true },
    { key: 'title', label: 'Stránka', width: 550, render: (_, row) => <><strong>{row.title}</strong><small>{row.page_path}</small></> },
    ...cols.map((column): DataColumn<PageRow> => ({
      key: column === 'views' ? 'views' : column === 'time' ? 'avg_time_on_page_sec' : 'bounce_rate',
      label: column === 'views' ? 'Zobrazení' : column === 'time' ? 'Průměrný čas' : 'Opuštění',
      width: column === 'views' ? 150 : 170,
      numeric: true,
      render: (_, row) => column === 'views' ? fmtNum(row.views) : column === 'time' ? fmtDuration(row.avg_time_on_page_sec) : fmtPct(row.bounce_rate, 1),
    })),
  ];

  const trendPoints = trend?.points ?? [];
  const labels = trendPoints.map((p) => p.date);
  const datasets = config.trend.map((t) => ({
    label: t.label,
    data: trendPoints.map((p) => {
      const raw = Number((p as unknown as Record<string, number>)[t.key] ?? 0);
      return t.fmt === 'pct' ? raw * 100 : raw;
    }),
    borderColor: t.color,
    fill: false,
  }));
  const tFirst = trendPoints[0];
  const tLast = trendPoints[trendPoints.length - 1];
  const trendRange = tFirst && tLast ? `${tFirst.date} – ${tLast.date} (${trendPoints.length} dní)` : 'žádná data';

  return (
    <section className={`activity-drill-page rondo-arc-activity-drill p1-arc-scope ${config.pageClass ?? ''}`}>
      {config.title && <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">AKTIVITA</div><h1>{config.title}</h1><p>{config.subtitle}</p></div></header>}
      <ArcToolbar className="rondo-arc-activity-toolbar" end={<small>Porovnání: {PERIOD_LABEL[period]} proti předchozímu období</small>}>
        <ArcSelect label="Období" value={period} onChange={(value) => setPeriod(value as AktivitaPeriod)} options={[{ value: 'day', label: 'Den' }, { value: 'week', label: 'Týden' }, { value: 'month', label: 'Měsíc' }, { value: 'year', label: 'Rok' }]} />
        <ArcSelect label="Trh" value={country} onChange={(value) => setCountry(value as AktivitaCountry)} options={[{ value: 'all', label: 'Vše' }, { value: 'cz', label: 'CZ' }, { value: 'sk', label: 'SK' }, { value: 'ro', label: 'RO' }]} />
        <Button variant="ghost" size="sm" type="button" onClick={reset}>Vyčistit</Button>
      </ArcToolbar>

      {error ? <PageState title="Data Aktivity se nepodařilo načíst" message={error} /> : (
        <div className="rondo-arc-activity-drill-kpis">
          {config.kpis.map((k) => {
            const curr = Number(kpi?.current?.[k.key] ?? 0);
            const prev = Number(kpi?.previous?.[k.key] ?? 0);
            const lib = k.lowerIsBetter || k.key === 'bounce_rate';
            return (
              <Card title={k.label} className="rondo-arc-activity-drill-kpi" key={k.key as string}>
                <strong>{fmtValue(curr, k.fmt)}</strong>
                <p><span>Minulé období {drillPrev(k, prev)}</span>{kpi ? <DeltaPlain curr={curr} prev={prev} lowerIsBetter={lib} /> : <span>—</span>}</p>
              </Card>
            );
          })}
        </div>
      )}

      <Card title={config.trendTitle ?? config.trend.map((item) => item.label).join(' a ')} description={trendRange} className="rondo-arc-activity-trend">
        <div>{datasets.length === 0 ? <PageState title="Zatím bez dat" /> : <TrendChart labels={labels} datasets={datasets} height={260} />}</div>
      </Card>

      <div className="rondo-arc-activity-pages-heading"><h2>{config.tableTitle}</h2><span>{pageRows.length} stránek</span></div>
      {loading ? <PageState title="Načítám stránky" loading /> : <div className="rondo-arc-activity-pages-table"><SortableDataTable rows={pageRows} columns={pageColumns} rowKey={(row) => `${row.page_path}-${row.rank}`} caption={config.tableTitle} emptyMessage="Žádné stránky pro toto období" /></div>}
    </section>
  );
}

function drillPrev(k: DrillKpi, prev: number): string {
  switch (k.fmt) {
    case 'duration':
      return fmtDuration(prev);
    case 'pct':
      return fmtPct(prev, 1);
    default:
      return fmtNum(prev);
  }
}

export function AktivitaNavstevnostPage() {
  return (
    <AktivitaDrillPage
      config={{
        title: 'Návštěvnost',
        subtitle: 'Vývoj návštěv, aktivních návštěvníků webu (GA4) a nejčtenějších stránek',
        pageClass: 'activity-visits-page',
        kpis: [
          { key: 'sessions', label: 'Návštěvy (sessions)', fmt: 'num' },
          { key: 'active_users', label: 'Aktivní návštěvníci (GA4)', fmt: 'num' },
          { key: 'new_users', label: 'Noví uživatelé', fmt: 'num' },
          { key: 'organic_search', label: 'Google vyhledávání', fmt: 'num' },
        ],
        trend: [
          { key: 'sessions', label: 'Návštěvy', color: '#1d4ed8' },
          { key: 'active_users', label: 'Aktivní návštěvníci (GA4)', color: '#0891b2' },
        ],
        trendTitle: 'Návštěvy a aktivní návštěvníci webu',
        pages: { sort: 'views', columns: ['views', 'time'], limit: 30 },
        tableTitle: 'Top stránky podle návštěv',
      }}
    />
  );
}

export function AktivitaZobrazeniPage() {
  return (
    <AktivitaDrillPage
      config={{
        title: 'Zobrazení',
        subtitle: 'Čtenost stránek, dosah obsahu a vztah mezi zobrazeními a návštěvami',
        pageClass: 'activity-views-page',
        kpis: [
          { key: 'page_views', label: 'Zobrazení stránek', fmt: 'num' },
          { key: 'sessions', label: 'Návštěvy', fmt: 'num' },
        ],
        trend: [
          { key: 'page_views', label: 'Zobrazení', color: '#137333' },
          { key: 'sessions', label: 'Návštěvy', color: '#1d4ed8' },
        ],
        trendTitle: 'Zobrazení stránek a návštěvy',
        pages: { sort: 'views', columns: ['views', 'time', 'bounce'], limit: 30 },
        tableTitle: 'Top stránky podle zobrazení',
      }}
    />
  );
}

const CAS_TABS: readonly ('cas' | 'pocasi')[] = ['cas', 'pocasi'];

export function AktivitaCasPage() {
  const [tab, setTab] = useTabParam<'cas' | 'pocasi'>('tab', CAS_TABS, 'cas');
  return (
    <section className="activity-time-workspace rondo-arc-activity-time p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">AKTIVITA</div><h1>Čas aktivity</h1><p>Délka návštěv a zapojení uživatelů v průběhu času</p></div></header>
      <ArcTabs label="Čas aktivity" value={tab} onChange={setTab} items={[{ value: 'cas', label: 'Čas aktivity' }, { value: 'pocasi', label: 'Počasí' }]} />
      {tab === 'cas' ? (
        <AktivitaDrillPage
          config={{
            pageClass: 'activity-time-page',
            kpis: [
              { key: 'avg_session_duration_sec', label: 'Avg. čas / session', fmt: 'duration' },
              { key: 'engagement_rate', label: 'Engagement rate', fmt: 'pct' },
            ],
            trend: [{ key: 'avg_session_duration_sec', label: 'Avg. čas (s)', color: '#92400e' }],
            trendTitle: 'Průměrný čas návštěvy',
            pages: { sort: 'time', columns: ['views', 'time'], limit: 30 },
            tableTitle: 'Top stránky podle průměrného času',
          }}
        />
      ) : (
        <Card title="Počasí" description="Korelace zapojení uživatelů s počasím se připravuje." />
      )}
    </section>
  );
}

export function AktivitaOpusteniPage() {
  return (
    <AktivitaDrillPage
      config={{
        title: 'Opuštění',
        subtitle: 'Míra opuštění, zapojení a stránky s největším odchodem uživatelů',
        pageClass: 'activity-bounce-page',
        kpis: [
          { key: 'bounce_rate', label: 'Bounce rate', fmt: 'pct', lowerIsBetter: true },
          { key: 'engagement_rate', label: 'Engagement rate', fmt: 'pct' },
        ],
        trend: [{ key: 'bounce_rate', label: 'Bounce (%)', color: '#b91c1c', fmt: 'pct' }],
        trendTitle: 'Vývoj míry opuštění',
        pages: { sort: 'bounce', columns: ['views', 'bounce'], limit: 30 },
        tableTitle: 'Stránky s nejvyšší mírou opuštění',
      }}
    />
  );
}
